import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Popconfirm, Select, Skeleton,
  Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  AssignStudentRouteRequest, BoardingPattern, StudentListQuery, StudentRouteAssignment,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

const BOARDING: BoardingPattern[] = ['both', 'pickup_only', 'drop_only'];

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/**
 * Admin — Transport ↔ Student assignments.
 * Feeds Parent bus tracking. Kept flat (one route per student for now).
 */
export function TransportAssignPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [routeFilter, setRouteFilter] = useState<string | undefined>(undefined);
  const [addOpen, setAddOpen] = useState(false);

  const list = useQuery({
    queryKey: ['transport-assignments', institution, routeFilter],
    queryFn: () => productApi.listTransportAssignments(institution, routeFilter),
    enabled: !!institution,
  });
  const routes = useQuery({
    queryKey: ['transport', institution],
    queryFn: () => productApi.getTransport(institution),
    enabled: !!institution,
  });
  const remove = useMutation({
    mutationFn: (studentId: string) => productApi.removeStudentFromRoute(institution, studentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transport-assignments', institution] });
      void message.success('Removed');
    },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Failed'),
  });

  if (list.isLoading || !list.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Transport assignments</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Assign students to a route + stop. Parent bus tracking reads this list.
          </Typography.Text>
        </div>
        <Select allowClear placeholder="Filter by route" style={{ width: 240 }} size="small"
          value={routeFilter}
          onChange={(v) => setRouteFilter(v ?? undefined)}
          options={(routes.data?.routes ?? []).map((r) => ({ value: r.id, label: `${r.code} · ${r.name}` }))} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}
          disabled={(routes.data?.routes.length ?? 0) === 0}>Assign student</Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card size="small"><Statistic title="Assigned" value={list.data.total} /></Card>
        <Card size="small"><Statistic title="Unassigned enrolled" value={list.data.unassigned_count}
          valueStyle={{ color: list.data.unassigned_count > 0 ? '#5b6478' : '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Active routes" value={(routes.data?.routes ?? []).filter((r) => r.active).length} /></Card>
      </div>

      <Card size="small">
        {list.data.rows.length === 0 ? (
          <Empty description={routeFilter ? 'No students on this route.' : 'No students assigned yet.'}
            style={{ padding: 20 }} />
        ) : (
          <Table<StudentRouteAssignment>
            size="small" pagination={{ pageSize: 25 }} rowKey="student_id"
            dataSource={list.data.rows}
            columns={[
              { title: 'Route', key: 'r', width: 200,
                render: (_, r) => <Space size={6}>
                  <Tag color="blue" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.route_code}</Tag>
                  <span style={{ fontSize: 12 }}>{r.route_name}</span>
                </Space> },
              { title: 'Stop', dataIndex: 'stop_name', key: 's' },
              { title: 'Student', key: 'st',
                render: (_, r) => <>
                  <b>{r.student_name}</b>
                  <div style={{ fontSize: 11, color: '#8a94a6', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                    {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}
                  </div>
                </> },
              { title: 'Boarding', dataIndex: 'boarding_pattern', key: 'b', width: 130,
                render: (v: BoardingPattern) => <Tag>{v.replace(/_/g, ' ')}</Tag> },
              { title: 'Vehicle', key: 'v', width: 160,
                render: (_, r) => r.vehicle_registration
                  ? <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{r.vehicle_registration}</span>
                  : <Typography.Text type="secondary">—</Typography.Text> },
              { title: '', key: 'act', width: 60,
                render: (_, r) => (
                  <Popconfirm title="Remove this assignment?" onConfirm={() => remove.mutate(r.student_id)}>
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                ) },
            ]}
          />
        )}
      </Card>

      <AssignModal open={addOpen} institution={institution}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['transport-assignments', institution] })}
        routes={routes.data?.routes ?? []} />
    </Space>
  );
}

function AssignModal({
  open, institution, onClose, onDone, routes,
}: {
  open: boolean; institution: string; onClose: () => void; onDone: () => void;
  routes: NonNullable<Awaited<ReturnType<typeof productApi.getTransport>>['routes']>;
}) {
  const [form] = Form.useForm<{ student_id: string; route_id: string; stop_name: string; boarding_pattern: BoardingPattern }>();
  const [search, setSearch] = useState('');
  const q: StudentListQuery = { q: search || undefined };
  const students = useQuery({
    queryKey: ['students', institution, q],
    queryFn: () => productApi.listStudents(institution, q),
    enabled: open && !!institution,
  });
  const m = useMutation({
    mutationFn: (input: AssignStudentRouteRequest) => productApi.assignStudentToRoute(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Assigned'); },
  });
  const err = extractErrMsg(m.error);

  return (
    <Modal open={open} title="Assign student to a route"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Assign" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ boarding_pattern: 'both' as BoardingPattern }}
        onFinish={(v) => m.mutate({
          student_id: v.student_id, route_id: v.route_id,
          stop_name: v.stop_name.trim(), boarding_pattern: v.boarding_pattern,
        })}>
        <Form.Item name="student_id" label="Student" rules={[{ required: true }]}>
          <Select showSearch filterOption={false} onSearch={setSearch}
            loading={students.isLoading}
            placeholder="Type name or admission number"
            options={(students.data?.rows ?? []).map((s) => ({
              value: s.id,
              label: `${s.name} · ${s.admission_number} · ${s.grade}${s.section ? `-${s.section}` : ''}`,
            }))} />
        </Form.Item>
        <Form.Item name="route_id" label="Route" rules={[{ required: true }]}>
          <Select options={routes.map((r) => ({ value: r.id, label: `${r.code} · ${r.name}` }))} />
        </Form.Item>
        <Form.Item name="stop_name" label="Stop" rules={[{ required: true, max: 160 }]}>
          <Input placeholder="e.g. Ring Road Junction" />
        </Form.Item>
        <Form.Item name="boarding_pattern" label="Boarding pattern" rules={[{ required: true }]}>
          <Select options={BOARDING.map((k) => ({ value: k, label: k.replace(/_/g, ' ') }))} />
        </Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}
